import { runCheckIn } from './checkIn';
import type { CheckInPorts, CheckInResult, ShellConfig } from './checkIn';
import { recordBootOutcome, syncBundle } from './bundles';
import type { BundleResult, UpdaterPort } from './bundles';
import { KEYS, safeGet, safeSet } from './storage';
import type { KeyValueStore } from './storage';

export const RECOMMEND_SNOOZE_MS = 24 * 60 * 60 * 1000;
export const INSTANT_BUDGET_MS = 3000;
/** A new sync waits for the previous one at most this long, so a hung download can never stall updates forever. */
export const IN_FLIGHT_WAIT_MS = 60_000;

// One bundle sync at a time per app process: a resume must not start a second
// download (or an apply racing the launch one) while the launch sync runs.
let inFlight: Promise<unknown> = Promise.resolve();

export interface ShellPorts extends Omit<CheckInPorts, 'currentBundleId'> {
  updater: UpdaterPort;
  sleep(ms: number): Promise<void>;
}

export type StartupDecision =
  | { screen: 'blocked'; storeUrl: string | null }
  | { screen: 'app'; recommendUpdate: boolean };

export interface StartupOutcome {
  decision: StartupDecision;
  checkIn: CheckInResult;
  /** Keeps running after the UI is shown; resolves when the bundle work is done. */
  bundleTask: Promise<BundleResult>;
}

export async function snoozeRecommendation(store: KeyValueStore, now: number): Promise<void> {
  await safeSet(store, KEYS.recommendSnoozeUntil, String(now + RECOMMEND_SNOOZE_MS));
}

/**
 * The update gate (spec §4.2). Call at every cold launch ('launch') and every
 * return to foreground ('resume'), BEFORE showing app screens.
 */
export async function runStartup(ports: ShellPorts, config: ShellConfig, phase: 'launch' | 'resume'): Promise<StartupOutcome> {
  const { store, updater } = ports;
  let nativeBuild: number | null = null;
  try { nativeBuild = await ports.device.nativeBuild(); } catch { /* unknown */ }
  if (phase === 'launch') await recordBootOutcome(store, updater, nativeBuild);

  const checkIn = await runCheckIn({ ...ports, currentBundleId: () => updater.currentBundleId() }, config);
  const r = checkIn.response;

  if (r.gate === 'required') {
    // Blocked phones update from the store first; new JS may not run on the old shell.
    return { decision: { screen: 'blocked', storeUrl: r.storeUrl }, checkIn, bundleTask: Promise.resolve('none') };
  }

  const snoozeUntil = Number(await safeGet(store, KEYS.recommendSnoozeUntil)) || 0;
  const decision: StartupDecision = { screen: 'app', recommendUpdate: r.gate === 'recommended' && ports.now() >= snoozeUntil };

  // Instant: apply now on resume, or at launch only if the download beats the
  // budget — a late download at launch must not reload the app mid-use.
  const deadline = ports.now() + INSTANT_BUDGET_MS;
  const applyNow = () => r.applyMode === 'instant' && (phase === 'resume' || ports.now() <= deadline);
  const previous = inFlight;
  const bundleTask: Promise<BundleResult> = Promise.race([previous, ports.sleep(IN_FLIGHT_WAIT_MS)])
    .catch(() => undefined)
    .then(() => syncBundle({ store, updater, nativeBuild }, r, applyNow))
    .catch((): BundleResult => 'failed');
  inFlight = bundleTask;

  if (r.applyMode === 'instant' && phase === 'launch') {
    await Promise.race([bundleTask, ports.sleep(INSTANT_BUDGET_MS)]);
  }
  return { decision, checkIn, bundleTask };
}
